import moment from "moment";
import { useIntl } from "react-intl";

import {
  Button,
  ButtonColors,
  ButtonTypes
} from "@certa/catalyst/components/Button";
import { Stack } from "@certa/catalyst/layouts/Stack";
import { Tooltip } from "@certa/catalyst/components/Tooltip";
import { showToast, ToastTypes } from "@certa/catalyst/components/Toast";
import { showAlertDialog } from "@certa/catalyst/components/AlertDialog";
import { Tag, TagColors, TagVariants } from "@certa/catalyst/components/Tag";
import { Ellipsis } from "@certa/catalyst/components/Typography";

import { Trashcan } from "@certa/icons/components/Trashcan";
import { Edit } from "@certa/icons/components/Edit";

import { useDeleteMassDelegation } from "@certa/queries/queries/massDelegation/massDelegation.query";

import { toCapital } from "../../utils/string";

import { type TransformedDelegationRequest } from "./DelegateTasks/DelegateTasks";
import {
  FILTER_TYPES,
  type FilterChangeHandlerFromFields
} from "@certa/datagrid";
import type { DELEGATION_REQUEST_FILTERS } from "./ManageDelegations";

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Scheduled", value: "scheduled" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" }
];

type UseDelegationColumnsParams = {
  onEditDelegationRequest: (
    delegationRequest: TransformedDelegationRequest
  ) => void;
  onFilterChange: FilterChangeHandlerFromFields<
    typeof DELEGATION_REQUEST_FILTERS
  >;
};

export const useManageDelegationColumns = ({
  onEditDelegationRequest,
  onFilterChange
}: UseDelegationColumnsParams) => {
  const intl = useIntl();

  return [
    {
      id: "name",
      header: intl.formatMessage({
        id: "fields.name",
        defaultMessage: "Name"
      }),
      accessorKey: "name",
      width: 200,
      canFilter: true,
      filterType: FILTER_TYPES.TEXT,
      onFilter: (value: string) => {
        onFilterChange("search", value);
      },
      cell: (props: { row: { original: TransformedDelegationRequest } }) => (
        <Ellipsis>{props.row.original.name}</Ellipsis>
      )
    },
    {
      id: "delegate",
      header: intl.formatMessage({
        id: "delegations.delegate",
        defaultMessage: "Delegate"
      }),
      accessorKey: "delegate",
      width: 200,
      canFilter: true,
      filterType: FILTER_TYPES.TEXT,
      onFilter: (value: string) => {
        onFilterChange("delegate", value);
      },
      cell: (props: { row: { original: TransformedDelegationRequest } }) => (
        <Ellipsis>{props.row.original.delegate}</Ellipsis>
      )
    },
    {
      id: "status",
      header: intl.formatMessage({
        id: "workflowFiltersTranslated.filterPlaceholders.status",
        defaultMessage: "Status"
      }),
      accessorKey: "status",
      width: 150,
      cell: (props: { row: { original: TransformedDelegationRequest } }) => (
        <ManageDelegationStatusCell status={props.row.original.status} />
      ),
      canFilter: true,
      filterType: FILTER_TYPES.SINGLE_SELECT,
      filterProps: {
        label: "Status",
        options: STATUS_OPTIONS
      },
      onFilter: (value: string) => {
        onFilterChange("status", value);
      }
    },
    {
      id: "startDate",
      header: intl.formatMessage({
        id: "delegations.startDate",
        defaultMessage: "Start Date"
      }),
      accessorKey: "startDate"
    },
    {
      id: "endDate",
      header: intl.formatMessage({
        id: "commonTextInstances.endDate",
        defaultMessage: "End Date"
      }),
      accessorKey: "endDate"
    },
    {
      id: "actions",
      header: intl.formatMessage({
        id: "common.actions",
        defaultMessage: "Actions"
      }),
      accessorKey: "actions",
      cell: (props: { row: { original: TransformedDelegationRequest } }) => (
        <ManageDelegationsActionsCell
          delegationRequest={props.row.original}
          onEditDelegationRequest={() =>
            onEditDelegationRequest(props.row.original)
          }
        />
      )
    }
  ];
};

type ManageDelegationsActionsCellProps = {
  delegationRequest: TransformedDelegationRequest;
  onEditDelegationRequest: () => void;
};

const ManageDelegationsActionsCell = (
  props: ManageDelegationsActionsCellProps
) => {
  const { delegationRequest, onEditDelegationRequest } = props;

  const {
    mutate: deleteDelegationRequest,
    isLoading: isDeletingDelegationRequest
  } = useDeleteMassDelegation({
    onSuccess: () => {
      showToast({
        title: "Delegation deleted",
        type: ToastTypes.SUCCESS
      });
    }
  });

  const onDeleteDelegationRequest = () => {
    showAlertDialog({
      title: "Do you want to stop this delegation?",
      type: "error",
      children: `${delegationRequest.delegate} will no longer be able to view your tasks and complete them on your behalf.`,
      primaryActionText: "Stop",
      onPrimaryAction: handleDeleteDelegationRequest
    });
  };

  const handleDeleteDelegationRequest = () => {
    deleteDelegationRequest(delegationRequest.id);
  };

  const isEditVisible =
    !moment().isSameOrAfter(delegationRequest.startDate) ||
    delegationRequest.status === "scheduled";

  const isStopDisabled = isDeletingDelegationRequest;

  const canDoDelegationActions =
    delegationRequest.status === "active" ||
    delegationRequest.status === "scheduled";

  if (!canDoDelegationActions) {
    return null;
  }

  return (
    <>
      <Stack direction="horizontal" gap="8" align="center">
        {isEditVisible && (
          <Tooltip content="Edit Delegation" showArrow={true}>
            <Button
              size="Small"
              variant="Outline"
              type={ButtonTypes.ICON}
              leftIcon={<Edit />}
              disabled={isDeletingDelegationRequest}
              onClick={onEditDelegationRequest}
            />
          </Tooltip>
        )}
        <Tooltip content="Stop Delegation" showArrow={true}>
          <Button
            size="Small"
            color={ButtonColors.RED}
            variant="Outline"
            type={ButtonTypes.ICON}
            leftIcon={<Trashcan />}
            onClick={onDeleteDelegationRequest}
            loading={isDeletingDelegationRequest}
            disabled={isStopDisabled}
          />
        </Tooltip>
      </Stack>
    </>
  );
};

const statusColorMap: Record<
  TransformedDelegationRequest["status"],
  TagColors
> = {
  scheduled: TagColors.TEAL,
  completed: TagColors.NEUTRAL,
  cancelled: TagColors.RED,
  active: TagColors.GREEN
};

type ManageDelegationStatusCellProps = {
  status: TransformedDelegationRequest["status"];
};

const ManageDelegationStatusCell = (props: ManageDelegationStatusCellProps) => {
  const { status } = props;

  const statusColor = statusColorMap[status];

  return (
    <Tag label={status} variant={TagVariants.FILLED} color={statusColor}>
      {toCapital(status)}
    </Tag>
  );
};
